/**
 * Named PostHog events, as requested by Jose for the sign-up funnel and the
 * account-form test.
 *
 * The flow already fires a tracking call at every one of these points for the
 * DiagnosisFlowEvent system, so rather than adding a second set of call sites
 * this maps those keys onto PostHog event names. track() forwards them before
 * its own pause check, so PostHog keeps receiving them while
 * DiagnosisFlowEvent stays switched off.
 *
 * Only the keys listed here are forwarded. Everything else the flow tracks
 * stays internal.
 */

export const PH_EVENTS = {
  // Sign-up, one per screen.
  signup_welcome: 'signup_welcome',
  signup_eligibility: 'signup_eligibility_check',
  signup_address: 'signup_location_check',
  // SignupFinalStep opens on the account form ("Save your progress"), and only
  // swaps to the code screen after it is submitted.
  signup_verify: 'signup_account_form',
  signup_code_sent: 'signup_code_sent',
  signup_code_submitted: 'signup_code_submitted',
  // /user/signup validates the code and creates the user in one call, so
  // "code verified" and "account created" are the same moment. Emitting one
  // event rather than two avoids a funnel step that is always 100%.
  // Jose asked whether a submitted code was right. signup_code_submitted
  // fires on every attempt; this pair says how it ended.
  signup_code_rejected: 'signup_code_rejected',
  signup_account_created: 'signup_account_created',
  checkout_success: 'payment_approved',
};

/** Events fired directly rather than through the flow's own tracking. */
export const PH_DIRECT = {
  STATE_NOT_SUPPORTED: 'signup_state_not_supported',
  CLICK: 'cta_click',
  // That a reset was asked for, and nothing about who asked. No email, no
  // user id.
  PASSWORD_RESET_REQUESTED: 'password_reset_requested',
  // A sign-in attempt that did not succeed.
  //
  // It deliberately does NOT say whether the account exists. The login
  // endpoint answers `code 101 "Invalid username/password."` for a wrong
  // password and for an address with no account, identically and on purpose,
  // so that it cannot be used to find out which addresses are registered.
  // Telling the two apart client-side would mean adding exactly that
  // oracle, so the event reports the failure and leaves the cause alone.
  LOGIN_FAILED: 'login_failed',
  // The one place the "no account" signal genuinely exists: the password
  // reset endpoint already answers whether the address is known, so this is
  // where people arriving without an account can actually be counted.
  PASSWORD_RESET_NO_ACCOUNT: 'password_reset_no_account',
  // Opening the reset screen, as distinct from actually requesting a reset.
  // Nothing recorded this before, so the step was invisible.
  FORGOT_PASSWORD_OPENED: 'forgot_password_opened',
  // The subscription offer inside checkout.
  SUBSCRIPTION_OFFER_SHOWN: 'subscription_offer_shown',
  SUBSCRIPTION_OFFER_ACCEPTED: 'subscription_offer_accepted',
  SUBSCRIPTION_OFFER_DECLINED: 'subscription_offer_declined',
  // Whether the patient took the priority upgrade. `chosen` says which way.
  PRIORITY_CHOSEN: 'priority_chosen',
};

/**
 * Where on the page a CTA was clicked. "Start treatment" appears three times on
 * the homepage alone, so without this they all arrive as one undifferentiated
 * count and there is no way to tell which placement actually works.
 */
export const PH_PLACEMENT = {
  HERO: 'hero',
  // Condition and sexual-health pages share one hero component, which was
  // never instrumented: only the homepage's Start treatment was reporting.
  CONDITION_PAGE: 'condition_page',
  SEXUAL_HEALTH_PAGE: 'sexual_health_page',
  MEDICATION_PAGE: 'medication_page',
  // The landing pages, as distinct from a single medication or condition.
  // Both reported nothing at all: /sexual-diseases and /medications had no
  // tracking on them, which is why their Start treatment clicks were missing
  // while the individual medication pages' were arriving.
  MEDICATION_LANDING: 'medication_landing',
  SEXUAL_HEALTH_LANDING: 'sexual_health_landing',
  CATEGORY_PAGE: 'category_page',
  COMPARISON_TABLE: 'comparison_table',
  HOW_IT_WORKS: 'how_it_works',
  BANNER: 'banner',
  // Jose's "checkout nav": the Log in button in the flow's progress header,
  // which sits above every step of the questionnaire and checkout.
  FLOW_HEADER: 'flow_header',
  // The modal that button opens, which has its own reset link. This is a
  // different control from the one on the flow's sign-in step, so the two
  // need telling apart.
  LOGIN_MODAL: 'login_modal',
  FLOW_SIGN_IN: 'flow_sign_in',
  // The account form at the end of sign-up ("Save your progress"), whose own
  // log-in link was the one control in the sequence still reporting nothing.
  FLOW_ACCOUNT_FORM: 'flow_account_form',
  // The site header, and the two modals it opens. "Already have an account,
  // log in" exists in the sign-up modal and on two sign-up steps inside the
  // flow, all sending the same cta, so without these three they arrive as
  // one count and cannot be told apart.
  HEADER: 'header',
  SIGNUP_MODAL: 'signup_modal',
  FLOW_SIGN_UP: 'flow_sign_up',
  SUBSCRIBE_BENEFITS: 'subscribe_benefits',
};

/**
 * Named CTAs, so the labels stay consistent between call sites.
 *
 * The three sign-up entry points are genuinely separate places in the header,
 * which is what Jose asked these to distinguish:
 *
 *   desktop_header  .headerLarge, the only sign-up on desktop
 *   mobile_icon     the account icon dropdown in .mainHeader
 *   mobile_menu     the slide-out .menuBar behind the hamburger
 *
 * .mainHeader is display:none above 768px and .headerLarge is hidden below
 * it, so a session only ever sees one of the two.
 *
 * Note there is no log-in control in the desktop header at all. Desktop
 * users reach log in through the sign-up modal's "Already Have An Account"
 * link, which is why that label carries more weight than it looks like it
 * should.
 */
export const PH_CTA = {
  DESKTOP_HEADER_SIGNUP: 'desktop_header_sign_up',
  MOBILE_ICON_SIGNUP: 'mobile_icon_sign_up',
  MOBILE_ICON_LOGIN: 'mobile_icon_log_in',
  MOBILE_MENU_SIGNUP: 'mobile_menu_sign_up',
  START_TREATMENT: 'start_treatment',
  ALREADY_HAVE_ACCOUNT: 'already_have_an_account_log_in',
  NO_ACCOUNT_SIGNUP: 'no_account_sign_up',
  // Sign-in and reset reached from inside the flow, which recorded nothing.
  FLOW_LOG_IN: 'flow_log_in',
  FORGOT_PASSWORD: 'forgot_password',
};

/**
 * Maps one of the flow's internal keys onto a PostHog event name, or returns
 * null when that key is not one Jose asked for.
 */
export function posthogEventFor(viewKey) {
  return PH_EVENTS[viewKey] ?? null;
}
